import { supabase } from "@/integrations/supabase/client";
import { Assessment } from "@/types/assessment";
import { getAssessmentById } from "./getAssessments";
import { saveCategoriesQuestionsAnswers } from "./saveCategoriesStructure";
import { hasContentChanges, createVersionHistoryEntry } from "./versionManagement";

// Update an assessment
export const updateAssessment = async (
  id: string,
  updates: Partial<Assessment>
): Promise<Assessment | null> => {
  try {
    // First get the current assessment to check version info
    const currentAssessment = await getAssessmentById(id);
    if (!currentAssessment) {
      return null;
    }

    // Determine if this is a content change that needs version increment
    const needsNewVersion =
      currentAssessment.status === "published" && hasContentChanges(currentAssessment, updates);

    // If this is a published assessment and content changes are being made,
    // save the current state to version history
    if (needsNewVersion) {
      const versionHistoryEntry = createVersionHistoryEntry(currentAssessment);

      const { error: versionError } = await supabase.from("version_history").insert({
        assessment_id: id,
        version_number: currentAssessment.version,
        categories_snapshot: JSON.stringify(versionHistoryEntry.categories),
      });

      if (versionError) {
        console.error("Error creating version history entry:", versionError);
        throw versionError;
      }
    }

    // Prepare assessment updates
    const dbUpdates: any = {};

    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
    if (updates.slug !== undefined) dbUpdates.slug = updates.slug;
    if (updates.notifyEmails !== undefined) dbUpdates.notify_emails = updates.notifyEmails;
    if (updates.calendlyLink !== undefined) dbUpdates.calendly_link = updates.calendlyLink;
    if (updates.assessmentType !== undefined) dbUpdates.assessment_type = updates.assessmentType;
    if (updates.status !== undefined) dbUpdates.status = updates.status;

    // Increment version if needed
    if (needsNewVersion) {
      dbUpdates.version = currentAssessment.version + 1;
    }

    // Always update timestamp
    dbUpdates.updated_at = new Date().toISOString();

    // Update assessment record
    if (Object.keys(dbUpdates).length > 0) {
      const { error: updateError } = await supabase
        .from("assessments")
        .update(dbUpdates)
        .eq("id", id);

      if (updateError) {
        console.error("Error updating assessment:", updateError);
        throw updateError;
      }
    }

    // If categories were provided, update them
    if (updates.categories) {
      await saveCategoriesQuestionsAnswers(id, updates.categories);
    }

    // Get the updated assessment
    return await getAssessmentById(id);
  } catch (error) {
    console.error("Error in updateAssessment:", error);
    throw error;
  }
};
